import React, { useMemo } from 'react';
import { Link, Typography, Box, Button, Tooltip, IconButton } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { GridCellParams } from '@mui/x-data-grid-pro';

import { translate as t } from 'hooks/useTranslations';
import { ActionMenu } from 'components/_atoms';
import { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import PhxBadge from '_lex/_atoms/PhxBadge';
import datetimeHelper from 'helpers/datetimeHelper';
import { DraftManualItem } from 'views/App/Portal/Policy/_types/manuals/draft';
import draftManualHelper from 'views/App/Portal/Policy/DraftManuals/helpers/draftManualHelper';

export type DraftManualsListViewProps = {
  manuals: DraftManualItem[];
  query: {
    isLoading?: boolean;
    error?: any;
    data?: any;
    isFetchingNextPage?: boolean;
    hasNextPage?: boolean;
    fetchNextPage?: () => Promise<any>;
    isError?: boolean;
  };
  totalCount: number;
  sorting: {
    sortModel: any[];
    onSortModelChange: (model: any) => void;
  };
  onExport: (manual: DraftManualItem) => void;
  onPublish: (manual: DraftManualItem) => void;
  getVerticalName: (verticalId: string) => string | undefined;
  manualHasPermissions: (manualId: string, permissions: string[]) => boolean;
  buildActionMenuItems: (manual: DraftManualItem) => ActionMenuItemProps[];
  isRiskPoolUser?: boolean;
};

const DraftManualsListView: React.FC<DraftManualsListViewProps> = ({
  manuals,
  query,
  sorting,
  onExport,
  getVerticalName,
  manualHasPermissions,
  buildActionMenuItems,
  isRiskPoolUser,
}) => {
  const columns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('policy.manuals.properties.name'),
        flex: 2,
        display: 'flex',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;

          const hasLockPermission = manualHasPermissions(row.id, ['lockDraftManual']);

          if (hasLockPermission) {
            return (
              <Link href={`./${row.id}`}>
                <Typography variant='h6' sx={{ fontWeight: 'semibold' }}>
                  {row.name}
                </Typography>
              </Link>
            );
          }
          return <Typography variant='h6'>{row.name}</Typography>;
        },
      },
      ...(isRiskPoolUser && {
        organization: {
          header: t('policy.manuals.properties.organization'),
          flex: 1.5,
          display: 'flex',
          renderer: (params: GridCellParams) => {
            const row = params.row;
            if (!row) return null;
            return <Typography>{row.organization?.name}</Typography>;
          },
        },
      }),
      verticalId: {
        header: t('policy.manuals.properties.vertical'),
        flex: 1,
        display: 'flex',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Typography>{getVerticalName(row.verticalId)}</Typography>;
        },
      },
      manualType: {
        header: t('policy.manuals.properties.manualType'),
        flex: 1,
        display: 'flex',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Typography>{row.manualType}</Typography>;
        },
      },
      publishedAt: {
        header: t('policy.manuals.properties.lastPublishedAt'),
        flex: 1,
        display: 'flex',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Typography>{datetimeHelper.formatDate(row.publishedAt)}</Typography>;
        },
      },
      policyDraftStates: {
        header: t('policy.manuals.properties.approvals'),
        flex: 1,
        display: 'flex',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          const approvedCount = draftManualHelper.getApprovedCount(row);
          if (approvedCount === 0) return null;
          return (
            <PhxBadge color='success'>
              {t('policy.manuals.properties.approvedPoliciesCount', '', { count: String(approvedCount) })}
            </PhxBadge>
          );
        },
      },
      actions: {
        header: '',
        type: 'actions',
        minWidth: 220,
        display: 'flex',
        columnProps: {
          pinned: 'right',
          hideable: false,
          sortable: false,
          resizable: false,
        },
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          const hasPublishPermission = manualHasPermissions(row.id, ['issuePolicy']);
          const approvedCount = draftManualHelper.getApprovedCount(row);
          const isPublishDisabled = approvedCount === 0;
          const publishButton = (
            <Button
              component={isPublishDisabled ? 'span' : Link}
              href={isPublishDisabled ? undefined : `./${row.id}/publish-approved`}
              variant='text'
              size='small'
              disabled={isPublishDisabled}
              endIcon={<ArrowForwardIcon />}
            >
              {t('draftManuals.actions.publish')}
            </Button>
          );
          return (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {hasPublishPermission && (
                isPublishDisabled ? (
                  <Tooltip title={t('draftManuals.actions.noApprovedPoliciesToPublish')}>
                    <span>{publishButton}</span>
                  </Tooltip>
                ) : (
                  publishButton
                )
              )}
              <Tooltip title={t('draftManuals.export.downloadTooltip', 'Export this manual or table of contents')}>
                <IconButton
                  onClick={() => onExport(row)}
                  aria-label={t('draftManuals.export.downloadTooltip', 'Export this manual or table of contents')}
                >
                  <SaveAltIcon />
                </IconButton>
              </Tooltip>
              <ActionMenu
                id={`action_menu_${row.id}`}
                items={buildActionMenuItems(row)}
              />
            </Box>
          );
        },
      },
    }),
    [getVerticalName, buildActionMenuItems, manualHasPermissions, onExport, isRiskPoolUser],
  );

  return (
    <GridSection
      columns={columns}
      data={manuals}
      query={query}
      sorting={sorting.sortModel}
      onSortModelChange={sorting.onSortModelChange}
    />
  );
};

export default DraftManualsListView;
